/**
 * demo.ts — the GUI v2 demo call (?demo=1 or no VITE_VOICE_WS_URL).
 *
 * Replays a realistic Tint King heat call that exercises every v2 surface:
 * caller-id seed → ghost name/vehicle extraction → reason chips → stage
 * progression → checklist sub-items + evidence → NBA + model nudges →
 * pricing resolution (truck class) → existing-tint add-on → objection →
 * compliance alert → buying signal → close motion → wrap-up.
 */
import type { CallCardState, ChecklistState, CoachingEvent } from "@/types";
import { freshCard, freshChecklist } from "@/live/state";

export interface DemoStep {
  at: number;
  event: CoachingEvent;
}

type CardPatch = (card: CallCardState) => void;

function card(...patches: CardPatch[]): CallCardState {
  const c = freshCard();
  c.phone = { value: "(317) 555-0142", confirmed: false, source: "caller_id" };
  for (const p of patches) p(c);
  return c;
}

const vehicleTacoma: CardPatch = (c) => {
  const v = c.vehicles[0];
  if (!v) return;
  v.year = { value: 2023, confirmed: false, source: "transcript" };
  v.make = { value: "Toyota", confirmed: false, source: "transcript" };
  v.model = { value: "Tacoma", confirmed: false, source: "transcript" };
  v.bodyStyle = { value: "truck", confirmed: false, source: "transcript" };
  v.pricingClass = "car_truck";
  v.classAssumed = true;
  c.serviceInterest = { value: "auto_tint", confirmed: false, source: "transcript" };
};

const vehicleConfirmed: CardPatch = (c) => {
  const v = c.vehicles[0];
  if (!v) return;
  v.year.confirmed = true;
  v.make.confirmed = true;
  v.model.confirmed = true;
  v.bodyStyle.confirmed = true;
  v.classAssumed = false;
  c.serviceInterest.confirmed = true;
};

const nameDana: CardPatch = (c) => {
  c.firstName = { value: "Dana", confirmed: false, source: "transcript" };
};
const nameDanaFull: CardPatch = (c) => {
  c.firstName = { value: "Dana", confirmed: true, source: "transcript" };
  c.lastName = { value: "Whitfield", confirmed: false, source: "transcript" };
};
const reasonHeat: CardPatch = (c) => {
  c.reasons = [{ key: "heat", confirmed: false }];
};
const reasonsHeatKids: CardPatch = (c) => {
  c.reasons = [
    { key: "heat", confirmed: true },
    { key: "kids", confirmed: false },
  ];
};
const existingTint: CardPatch = (c) => {
  const v = c.vehicles[0];
  if (v) v.existingTint = true;
};

function checklist(overrides: Partial<Record<string, boolean>>, subs?: { name?: boolean; vehicle?: boolean; reason?: boolean }): ChecklistState {
  return freshChecklist().map((item) => {
    const covered = overrides[item.key] ?? false;
    const subItems = item.subItems
      ? item.subItems.map((s) => ({
          ...s,
          covered: subs?.[s.key] ?? covered,
          evidence: (subs?.[s.key] ?? covered) ? { text: "I've got a 2023 Tacoma", speaker: "customer" as const, atMs: 9000 } : null,
        }))
      : null;
    return {
      ...item,
      covered,
      coveredAtMs: covered ? 1000 : null,
      evidence: covered ? { text: "demo evidence line", speaker: "rep" as const, atMs: 1000 } : null,
      subItems,
    };
  });
}

const t = (speaker: "rep" | "customer", text: string, startMs: number): CoachingEvent => ({
  type: "transcript",
  line: { speaker, text, startMs, final: true },
});

export const DEMO_SEQUENCE: DemoStep[] = [
  { at: 200, event: { type: "call_ringing", counterpart: "(317) 555-0142" } },
  { at: 1600, event: { type: "call_started", startedAt: new Date().toISOString(), counterpart: "(317) 555-0142" } },
  { at: 1900, event: { type: "call_card", card: card() } },
  { at: 2000, event: t("rep", "Thanks for calling Tint King, this is Marcus. How can I help you today?", 1200) },
  { at: 2200, event: t("customer", "Hey, how much do you guys charge to tint a truck?", 2000) },
  // NBA rule 1: price question before capture.
  {
    at: 2700,
    event: {
      type: "nudge",
      nudge: {
        id: "d1", text: "Capture name and phone before quoting.", criterionKey: "captured_customer_vehicle_reasons",
        severity: "prompt", atMs: 2500, ttlMs: 14000, kind: "process", promptId: "p4",
        sourceRef: "24_live-ai-coaching-prompts.md §C rule 1",
        evidence: { text: "how much do you guys charge to tint a truck?", speaker: "customer", atMs: 2000 },
      },
    },
  },
  { at: 3500, event: { type: "stage", stageId: 2 } },
  { at: 4200, event: t("rep", "Glad you called — I can get you an exact price. Who am I talking with?", 4000) },
  { at: 5200, event: t("customer", "Dana.", 5000) },
  { at: 5600, event: { type: "call_card", card: card(nameDana) } },
  { at: 5900, event: { type: "behavior", key: "name_captured", evidence: { text: "Dana.", speaker: "customer", atMs: 5000 } } },
  { at: 6600, event: { type: "stage", stageId: 3 } },
  { at: 7400, event: t("rep", "Great to meet you, Dana. What's the year, make, and model?", 7200) },
  { at: 9200, event: t("customer", "It's a 2023 Tacoma.", 9000) },
  { at: 9700, event: { type: "call_card", card: card(nameDana, vehicleTacoma, reasonHeat) } },
  { at: 10000, event: { type: "behavior", key: "vehicle_captured", evidence: { text: "It's a 2023 Tacoma.", speaker: "customer", atMs: 9000 } } },
  { at: 10600, event: { type: "checklist", checklist: checklist({}, { name: true, vehicle: true }) } },
  { at: 11400, event: t("customer", "Honestly the heat is brutal — it sits out all day at the hospital lot.", 11200) },
  { at: 12000, event: { type: "stage", stageId: 5 } },
  {
    at: 12600,
    event: {
      type: "nudge",
      nudge: {
        id: "d2", text: "Ask if kids or passengers ride in back.", criterionKey: "captured_customer_vehicle_reasons",
        severity: "info", atMs: 12400, ttlMs: 12000, kind: "discovery", promptId: "p19",
        sourceRef: "09_discovery-question-bank.md #14", evidence: { text: "the heat is brutal", speaker: "customer", atMs: 11200 },
      },
    },
  },
  { at: 14000, event: t("customer", "Yeah, my daughter rides in the back after school.", 13800) },
  { at: 14500, event: { type: "call_card", card: card(nameDanaFull, vehicleTacoma, reasonsHeatKids) } },
  { at: 14800, event: { type: "behavior", key: "motivation_captured", evidence: { text: "the heat is brutal", speaker: "customer", atMs: 11200 } } },
  { at: 15200, event: { type: "checklist", checklist: checklist({ captured_customer_vehicle_reasons: true }, { name: true, vehicle: true, reason: true }) } },
  { at: 16000, event: t("customer", "Oh and there's some old tint on the back glass already.", 15800) },
  { at: 16500, event: { type: "call_card", card: card(nameDanaFull, vehicleTacoma, reasonsHeatKids, existingTint) } },
  {
    at: 17000,
    event: {
      type: "nudge",
      nudge: {
        id: "d3", text: "Old tint: removal is $125 per hour, quoted up front.", criterionKey: null,
        severity: "prompt", atMs: 16800, ttlMs: 12000, kind: "process", promptId: "p11",
        sourceRef: "24_live-ai-coaching-prompts.md §C rule 3",
        evidence: { text: "there's some old tint on the back glass", speaker: "customer", atMs: 15800 },
      },
    },
  },
  { at: 18500, event: { type: "stage", stageId: 7 } },
  { at: 19500, event: t("rep", "Most people don't realize the right film is as much about heat as looks — our ceramic lines block up to 71 percent of solar energy depending on shade.", 19300) },
  { at: 20000, event: { type: "behavior", key: "education_moment", evidence: { text: "as much about heat as looks", speaker: "rep", atMs: 19300 } } },
  { at: 20600, event: t("rep", "For your Tacoma, heat being the story, i3 is the one I'd put on my own truck.", 20400) },
  { at: 21100, event: { type: "behavior", key: "recommendation_made", evidence: { text: "i3 is the one I'd put on my own truck", speaker: "rep", atMs: 20400 } } },
  { at: 21400, event: { type: "behavior", key: "premium_offered", evidence: { text: "i3 is the one I'd put on my own truck", speaker: "rep", atMs: 20400 } } },
  { at: 21800, event: { type: "checklist", checklist: checklist({ captured_customer_vehicle_reasons: true, offered_premium: true }, { name: true, vehicle: true, reason: true }) } },
  { at: 22600, event: { type: "stage", stageId: 8 } },
  { at: 23400, event: t("rep", "It carries our Life Happens Warranty — unlimited lifetime no-fault on i3.", 23200) },
  { at: 23900, event: { type: "behavior", key: "warranty_named", evidence: { text: "Life Happens Warranty — unlimited lifetime", speaker: "rep", atMs: 23200 } } },
  { at: 24300, event: { type: "checklist", checklist: checklist({ captured_customer_vehicle_reasons: true, offered_premium: true, built_value_usp: true }, { name: true, vehicle: true, reason: true }) } },
  // Compliance slip: a fuel payback promise.
  { at: 25400, event: t("rep", "Honestly it basically pays for itself in gas savings.", 25200) },
  { at: 25800, event: { type: "compliance_alert", alertId: "fuel_numbers", evidence: { text: "it basically pays for itself in gas savings", speaker: "rep", atMs: 25200 } } },
  { at: 27200, event: { type: "stage", stageId: 9 } },
  { at: 28000, event: t("rep", "i3 on your Tacoma, full sides and rear, is $749 even — flat rate, confirmed at drop-off.", 27800) },
  { at: 28400, event: { type: "behavior", key: "price_stated", evidence: { text: "full sides and rear, is $749 even", speaker: "rep", atMs: 27800 } } },
  // Objection: cheaper quote.
  { at: 29800, event: t("customer", "Another shop quoted me 500 for ceramic…", 29600) },
  { at: 30200, event: { type: "stage", stageId: 10 } },
  { at: 30500, event: { type: "objection", objectionId: "cheaper-tint-quote", evidence: { text: "Another shop quoted me 500 for ceramic", speaker: "customer", atMs: 29600 } } },
  { at: 33500, event: t("customer", "Okay, that makes sense. When could you get me in?", 33300) },
  { at: 33900, event: { type: "buying_signal", evidence: { text: "When could you get me in?", speaker: "customer", atMs: 33300 } } },
  { at: 34300, event: { type: "stage", stageId: 11 } },
  { at: 36000, event: t("rep", "I've got Tuesday morning or Thursday afternoon — which works better? It's a $100 deposit and it comes right off the $749.", 35800) },
  { at: 36400, event: { type: "behavior", key: "two_times_offered", evidence: { text: "Tuesday morning or Thursday afternoon", speaker: "rep", atMs: 35800 } } },
  { at: 36700, event: { type: "behavior", key: "deposit_asked", evidence: { text: "$100 deposit and it comes right off the $749", speaker: "rep", atMs: 35800 } } },
  { at: 38500, event: t("customer", "Thursday works. Send me the link.", 38300) },
  { at: 39200, event: { type: "behavior", key: "booking_confirmed", evidence: { text: "Thursday works. Send me the link.", speaker: "customer", atMs: 38300 } } },
  { at: 39600, event: { type: "checklist", checklist: checklist({ captured_customer_vehicle_reasons: true, offered_premium: true, built_value_usp: true, confirm_booking: true }, { name: true, vehicle: true, reason: true }) } },
  { at: 40400, event: { type: "stage", stageId: 12 } },
  { at: 41200, event: t("rep", "Read it back: Dana Whitfield, 2023 Tacoma, i3 full sides and rear, Thursday, drop off by 11. Confirmation text on its way.", 41000) },
  { at: 41600, event: { type: "behavior", key: "readback_done", evidence: { text: "Read it back: Dana Whitfield, 2023 Tacoma…", speaker: "rep", atMs: 41000 } } },
  // Read-back = "AI heard it repeated" → fields confirm (req 1.4).
  { at: 42000, event: { type: "call_card", card: card(nameDanaFull, vehicleTacoma, vehicleConfirmed, reasonsHeatKids, existingTint) } },
  { at: 43500, event: { type: "call_ended", endedAt: new Date().toISOString() } },
];
